import React, {useMemo} from "react";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import {InputText} from "primereact/inputtext";
import {InputSwitch} from "primereact/inputswitch";
import {Divider} from "primereact/divider";
import {Button} from "primereact/button";
import {useAppDispatch, useAppSelector} from "redux/hooks";
import {setTrunkField, setTrunkErrorField} from "../../../../redux/actions/actionsTrunk";
import {
    API_TRUNK_IN_PREFIX_DEFAULT,
    API_TRUNK_OUT_PREFIX_DEFAULT, findDefaultTrunkSettingsRowById,
    normalizeTrunkInPrefix,
    normalizeTrunkOutPrefix, trunkPrefixesFromDefaultSettingsRow,
} from "../../../../redux/api/apiTrunks";
import {classNames} from "primereact/utils";

type OutKey = keyof typeof API_TRUNK_OUT_PREFIX_DEFAULT;
type InKey = keyof typeof API_TRUNK_IN_PREFIX_DEFAULT;

type ProviderOption = {id: number | string; name: string};

function normalizeIdForDropdown(
    raw: unknown,
    options: ProviderOption[] | null | undefined
): number | string | null {
    if (raw === null || raw === undefined || raw === "") return null;
    if (!options?.length) {
        const n = Number(raw);
        return Number.isNaN(n) ? (raw as string) : n;
    }
    const sampleId = options[0]?.id;
    if (typeof sampleId === "number") {
        const n = Number(raw);
        return Number.isNaN(n) ? null : n;
    }
    if (typeof sampleId === "string") {
        return String(raw);
    }
    const n = Number(raw);
    return Number.isNaN(n) ? (raw as string) : n;
}

function mapRowsToProviderOptions(rows: unknown): ProviderOption[] {
    if (!Array.isArray(rows)) return [];
    const out: ProviderOption[] = [];
    const seen = new Set<string>();
    for (const item of rows) {
        if (item == null || typeof item !== "object") continue;
        const o = item as Record<string, unknown>;
        const id =
            o.providerLibraryId ??
            o.ProviderLibraryId ??
            o.id ??
            o.sofiaTrunkId ??
            o.defaultTrunkId ??
            o.DefaultTrunkId ??
            o.trunkId ??
            o.TrunkId;
        if (id === undefined || id === null || id === "") continue;
        const name =
            (typeof o.gatewayName === "string" && o.gatewayName) ||
            (typeof o.profileName === "string" && o.profileName) ||
            (typeof o.name === "string" && o.name) ||
            (typeof o.trunkName === "string" && o.trunkName) ||
            String(id);
        const key = String(id);
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({id: id as number | string, name});
    }
    return out;
}

const OUT_FIELDS: {key: Exclude<OutKey, "isPai">; label: string; kind: "count" | "text"}[] = [
    {key: "did_Count", label: "[Did_Count]", kind: "count"},
    {key: "did_Prefix", label: "[Did_Prefix]", kind: "text"},
    {key: "pai_Count", label: "[Pai_Count]", kind: "count"},
    {key: "pai_Prefix", label: "[Pai_Prefix]", kind: "text"},
    {key: "cid_Count", label: "[Cid_Count]", kind: "count"},
    {key: "cid_Prefix", label: "[Cid_Prefix]", kind: "text"},
    {key: "cid_Number", label: "[Cid_Number]", kind: "text"},
    {key: "pai_Number", label: "[Pai_Number]", kind: "text"},
];

const IN_FIELDS: {key: InKey; label: string}[] = [
    {key: "isPai", label: "[IsPai]"},
    {key: "isDiversions", label: "[IsDiversions]"},
];

const TrunkConfigurationTab = () => {
    const dispatch = useAppDispatch();
    const trunkData = useAppSelector((state) => state.TrunksListReducer.trunkData);
    const trunksList = useAppSelector(state => state.TrunksListReducer.trunksList);
    const errorFields = useAppSelector(state => state.TrunksListReducer.errorFields);
    const defaultTrunkSettingsList = useAppSelector(
        (state) => state.TrunksListReducer.defaultTrunkSettingsList
    );
    const editType = useAppSelector((state) => state.UsersReducer.editType);
    const archiveTableToggle = useAppSelector(
        (state) => state.TrunksListReducer.archiveTrunkTableToggle
    );

    const providerOptions = useMemo(
        () => mapRowsToProviderOptions(defaultTrunkSettingsList),
        [defaultTrunkSettingsList]
    );

    const rawProviderId = trunkData?.providerLibraryId ?? null;
    const providerValue = normalizeIdForDropdown(rawProviderId, providerOptions);

    const out = normalizeTrunkOutPrefix(trunkData?.outPrefix) as {
        [K in OutKey]: (typeof API_TRUNK_OUT_PREFIX_DEFAULT)[K];
    };
    const inn = normalizeTrunkInPrefix(trunkData?.inPrefix) as {
        [K in InKey]: (typeof API_TRUNK_IN_PREFIX_DEFAULT)[K];
    };

    const disabled = !archiveTableToggle || !editType;

    const patchOut = (patch: Partial<typeof API_TRUNK_OUT_PREFIX_DEFAULT>) => {
        dispatch(
            setTrunkField({
                section: "trunk",
                fieldName: "outPrefix",
                value: {...out, ...patch},
            })
        );
    };

    const patchIn = (patch: Partial<typeof API_TRUNK_IN_PREFIX_DEFAULT>) => {
        dispatch(
            setTrunkField({
                section: "trunk",
                fieldName: "inPrefix",
                value: {...inn, ...patch},
            })
        );
    };

    type TrunkOption = {id: number | string; name: string};

    function mapRowsToDefaultTrunkOptions(rows: unknown): TrunkOption[] {
        if (!Array.isArray(rows)) return [];
        const out: TrunkOption[] = [];
        for (const item of rows) {
            if (item == null) continue;
            if (typeof item === "number") {
                out.push({id: item, name: String(item)});
                continue;
            }
            if (typeof item !== "object") continue;
            const o = item as Record<string, unknown>;
            const id =
                o.defaultTrunkId ??
                o.DefaultTrunkId ??
                o.id ??
                o.trunkId ??
                o.TrunkId ??
                o.sofiaTrunkId;
            if (id === undefined || id === null || id === "") continue;
            const name =
                (typeof o.gatewayName === "string" && o.gatewayName) ||
                (typeof o.name === "string" && o.name) ||
                (typeof o.trunkName === "string" && o.trunkName) ||
                `Транк ${id}`;
            out.push({id: id as number | string, name});
        }
        return out;
    }

    const defaultTrunkOptions = useMemo(() => {
        const fromDefaults = mapRowsToDefaultTrunkOptions(defaultTrunkSettingsList);
        if (fromDefaults.length > 0) return fromDefaults;
        if (Array.isArray(trunksList) && trunksList.length > 0) {
            return trunksList
                .map((row: {id?: number; name?: string}) => {
                    if (row?.id == null) return null;
                    return {
                        id: row.id,
                        name: row.name ?? String(row.id),
                    } as TrunkOption;
                })
                .filter((x): x is TrunkOption => x != null);
        }
        return [];
    }, [defaultTrunkSettingsList, trunksList]);

    const rawDefaultTrunkId = trunkData?.defaultTrunkId ?? null;
    const defaultTrunkIdValue = normalizeIdForDropdown(
        rawDefaultTrunkId,
        defaultTrunkOptions
    );

    const applyDefaultsFromSelectedDefaultTrunk = () => {
        const id = defaultTrunkIdValue;
        if (id == null) return;
        const row = findDefaultTrunkSettingsRowById(
            defaultTrunkSettingsList,
            id
        );
        const applied = row
            ? trunkPrefixesFromDefaultSettingsRow(row)
            : null;
        if (!applied) return;
        dispatch(
            setTrunkField({
                section: "trunk",
                fieldName: "outPrefix",
                value: applied.outPrefix,
            })
        );
        dispatch(
            setTrunkField({
                section: "trunk",
                fieldName: "inPrefix",
                value: applied.inPrefix,
            })
        );
        dispatch(
            setTrunkField({
                section: "trunk",
                fieldName: "needPaiNumber",
                value: applied.needPaiNumber,
            })
        );
        dispatch(
            setTrunkField({
                section: "trunk",
                fieldName: "providerLibraryId",
                value: applied.providerLibraryId,
            })
        );
    };

    return (
        <div className="general-settings">
            <div className="grid p-fluid">
                <div className="field col-12 md:col-4">
                    <label
                        htmlFor="providerLibrary"
                        className={classNames({
                            "p-error": errorFields?.defaultTrunkId && !defaultTrunkIdValue,
                        })}
                    >
                        Бібліотека провайдерів
                    </label>
                    <div className="flex flex-column md:flex-row gap-2 w-full align-items-stretch md:align-items-end">
                    <Dropdown
                        id="defaultTrunkId"
                        value={defaultTrunkIdValue}
                        options={defaultTrunkOptions}
                        filter
                        className={classNames("w-full flex-1", {
                            "p-invalid": errorFields?.defaultTrunkId && !defaultTrunkIdValue,
                        })}
                        optionValue="id"
                        optionLabel="name"
                        placeholder="Оберіть транк"
                        onChange={(e) => {
                            const newId = e.value ?? null;
                            if (newId != null && errorFields?.defaultTrunkId) {
                                const rest = {
                                    ...(errorFields as Record<string, unknown>),
                                };
                                delete rest.defaultTrunkId;
                                dispatch(
                                    setTrunkErrorField(
                                        Object.keys(rest).length ? rest : null
                                    )
                                );
                            }
                            dispatch(
                                setTrunkField({
                                    section: "trunk",
                                    fieldName: "defaultTrunkId",
                                    value: newId,
                                })
                            );
                            if (newId == null) {
                                dispatch(
                                    setTrunkField({
                                        section: "trunk",
                                        fieldName: "outPrefix",
                                        value: normalizeTrunkOutPrefix(null),
                                    })
                                );
                                dispatch(
                                    setTrunkField({
                                        section: "trunk",
                                        fieldName: "inPrefix",
                                        value: normalizeTrunkInPrefix(null),
                                    })
                                );
                                dispatch(
                                    setTrunkField({
                                        section: "trunk",
                                        fieldName: "needPaiNumber",
                                        value: false,
                                    })
                                );
                                dispatch(
                                    setTrunkField({
                                        section: "trunk",
                                        fieldName: "providerLibraryId",
                                        value: null,
                                    })
                                );
                                return;
                            }
                            const row = findDefaultTrunkSettingsRowById(
                                defaultTrunkSettingsList,
                                newId
                            );
                            const applied = row
                                ? trunkPrefixesFromDefaultSettingsRow(row)
                                : null;
                            if (!applied) return;
                            dispatch(
                                setTrunkField({
                                    section: "trunk",
                                    fieldName: "outPrefix",
                                    value: applied.outPrefix,
                                })
                            );
                            dispatch(
                                setTrunkField({
                                    section: "trunk",
                                    fieldName: "inPrefix",
                                    value: applied.inPrefix,
                                })
                            );
                            dispatch(
                                setTrunkField({
                                    section: "trunk",
                                    fieldName: "needPaiNumber",
                                    value: applied.needPaiNumber,
                                })
                            );
                            dispatch(
                                setTrunkField({
                                    section: "trunk",
                                    fieldName: "providerLibraryId",
                                    value: applied.providerLibraryId,
                                })
                            );
                        }}
                        disabled={!archiveTableToggle || !editType || trunkData?.id}
                        // disabled={!archiveTableToggle || !editType}

                    />
                    </div>
                    {errorFields?.defaultTrunkId && (
                        <div className="col-12 pt-2 px-0 pb-0">
                            <span className="title p-error text-sm">
                                {
                                    (errorFields.defaultTrunkId as {defaultTrunkId?: string})
                                        ?.defaultTrunkId
                                }
                            </span>
                        </div>
                    )}
                </div>
                <div className="col-4 field">
                    <label
                        htmlFor="providerLibrary"
                        className="block"
                        style={{height: '4px'}}
                    >
                    </label>
                <Button
                    type="button"
                    icon="pi pi-replay"
                    label="Дефолтні налаштування"
                    className="flex-shrink-0 mt-3 w-16rem"
                    disabled={
                        disabled ||
                        !defaultTrunkIdValue
                    }
                    onClick={applyDefaultsFromSelectedDefaultTrunk}
                />
                </div>
                {/*<Divider className="col-12 mx-1"/>*/}
                {/*<div className="field col-12 md:col-4">*/}
                {/*    <label htmlFor="needPaiNumber">[needPaiNumber]</label>*/}
                {/*    <div className="flex align-items-center" style={{minHeight: "2.5rem"}}>*/}
                {/*        <InputSwitch*/}
                {/*            inputId="needPaiNumber"*/}
                {/*            checked={Boolean(trunkData?.needPaiNumber)}*/}
                {/*            disabled={disabled}*/}
                {/*            onChange={(e) => {*/}
                {/*                dispatch(*/}
                {/*                    setTrunkField({*/}
                {/*                        section: "trunk",*/}
                {/*                        fieldName: "needPaiNumber",*/}
                {/*                        value: e.value,*/}
                {/*                    })*/}
                {/*                );*/}
                {/*            }}*/}
                {/*        />*/}
                {/*    </div>*/}
                {/*</div>*/}
                <Divider className="col-12 mx-1"/>
                <div className="col-12">
                    <span className="font-bold">InPrefix</span>
                </div>
                {IN_FIELDS.map(({key, label}) => (
                    <div className="field col-12 md:col-4" key={key}>
                        <label htmlFor={`in_${key}`}>{label}</label>
                        <div className="flex align-items-center" style={{minHeight: "2.5rem"}}>
                            <InputSwitch
                                inputId={`in_${key}`}
                                checked={Boolean(inn[key])}
                                disabled={disabled}
                                onChange={(e) =>
                                    patchIn({[key]: e.value} as Partial<
                                        typeof API_TRUNK_IN_PREFIX_DEFAULT
                                    >)
                                }
                            />
                        </div>
                    </div>
                ))}
                <Divider className="col-12 mx-1"/>
                <div className="col-12">
                    <span className="font-bold">OutPrefix</span>
                </div>
                <div className="field col-12">
                    <label htmlFor="out_isPai">[IsPai]</label>
                    <div className="flex align-items-center" style={{minHeight: "2.5rem"}}>
                        <InputSwitch
                            inputId="out_isPai"
                            checked={Boolean(out.isPai)}
                            disabled={disabled}
                            onChange={(e) =>
                                patchOut({
                                    isPai: e.value,
                                } as Partial<typeof API_TRUNK_OUT_PREFIX_DEFAULT>)
                            }
                        />
                    </div>
                </div>
                <div className="col-12 field flex flex-wrap">
                {OUT_FIELDS.map(({key, label, kind}) => (
                    <div className="field col-12 md:col-3" key={key}>
                        <label htmlFor={`out_${key}`}>{label}</label>
                        {kind === "count" && (
                            <InputNumber
                                id={`out_${key}`}
                                className="w-full"
                                inputClassName="w-full"
                                useGrouping={false}
                                value={out[key] as number}
                                disabled={disabled}
                                onValueChange={(e) =>
                                    patchOut({[key]: e.value ?? 0} as Partial<
                                        typeof API_TRUNK_OUT_PREFIX_DEFAULT
                                    >)
                                }
                            />
                        )}
                        {kind === "text" && (
                            <>
                                <InputText
                                    id={`out_${key}`}
                                    className={classNames("w-full", {
                                        "p-invalid":
                                            key === "pai_Number" &&
                                            Boolean(errorFields?.paiNumber),
                                    })}
                                    value={String(out[key] ?? "")}
                                    disabled={disabled}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        if (
                                            key === "pai_Number" &&
                                            v.trim() &&
                                            errorFields?.paiNumber
                                        ) {
                                            const rest = {
                                                ...(errorFields as Record<string, unknown>),
                                            };
                                            delete rest.paiNumber;
                                            dispatch(
                                                setTrunkErrorField(
                                                    Object.keys(rest).length
                                                        ? rest
                                                        : null
                                                )
                                            );
                                        }
                                        patchOut({[key]: v} as Partial<
                                            typeof API_TRUNK_OUT_PREFIX_DEFAULT
                                        >);
                                    }}
                                />
                                {key === "pai_Number" &&
                                    (errorFields?.paiNumber as {pai_Number?: string} | undefined)
                                        ?.pai_Number && (
                                        <small className="p-error block mt-1">
                                            {
                                                (errorFields.paiNumber as {pai_Number: string})
                                                    .pai_Number
                                            }
                                        </small>
                                    )}
                            </>
                        )}
                    </div>
                ))}
                </div>
            </div>
        </div>
    );
};

export default TrunkConfigurationTab;
